import React, { createContext, useContext, useState, ReactNode } from 'react';
import { CloudinaryImage } from '../types/Gallery';
import ImageSelectionModal from '../components/ImageSelection/ImageSelectionModal';

interface ImageSelectionContextType {
  isImageSelectionOpen: boolean;
  selectedImages: CloudinaryImage[];
  openImageSelection: (config: ImageSelectionConfig) => void;
  closeImageSelection: () => void;
  updateSelectedImages: (images: CloudinaryImage[]) => void;
  clearSelectedImages: () => void;
}

interface ImageSelectionConfig {
  onImagesSelected: (images: CloudinaryImage[]) => void;
  existingImages?: CloudinaryImage[];
  maxSelection?: number;
  title?: string;
  allowUpload?: boolean;
  showThumbnails?: boolean;
}

const ImageSelectionContext = createContext<ImageSelectionContextType | undefined>(undefined);

export const useImageSelection = () => {
  const context = useContext(ImageSelectionContext);
  if (!context) {
    throw new Error('useImageSelection must be used within an ImageSelectionProvider');
  }
  return context;
};

interface ImageSelectionProviderProps {
  children: ReactNode;
}

export const ImageSelectionProvider: React.FC<ImageSelectionProviderProps> = ({ children }) => {
  const [isImageSelectionOpen, setIsImageSelectionOpen] = useState(false);
  const [selectedImages, setSelectedImages] = useState<CloudinaryImage[]>([]);
  const [config, setConfig] = useState<ImageSelectionConfig | null>(null);

  const openImageSelection = (newConfig: ImageSelectionConfig) => {
    setConfig(newConfig);
    setSelectedImages([]);
    setIsImageSelectionOpen(true);
  };

  const closeImageSelection = () => {
    setIsImageSelectionOpen(false);
    setSelectedImages([]);
    setConfig(null);
  };

  const updateSelectedImages = (images: CloudinaryImage[]) => {
    setSelectedImages(images);
  };

  const clearSelectedImages = () => {
    setSelectedImages([]);
  };

  const handleImagesSelected = (images: CloudinaryImage[]) => {
    if (config?.onImagesSelected) {
      config.onImagesSelected(images);
    }
    closeImageSelection();
  };

  const value: ImageSelectionContextType = {
    isImageSelectionOpen,
    selectedImages,
    openImageSelection,
    closeImageSelection,
    updateSelectedImages,
    clearSelectedImages,
  };

  return (
    <ImageSelectionContext.Provider value={value}>
      {children}
      {/* Image Selection Modal */}
      {isImageSelectionOpen && config && (
        <ImageSelectionModal
          isOpen={isImageSelectionOpen}
          onClose={closeImageSelection}
          onImagesSelected={handleImagesSelected}
          existingImages={config.existingImages || []}
          maxSelection={config.maxSelection || 50}
          title={config.title || 'Select Images'}
          allowUpload={config.allowUpload !== false}
          showThumbnails={config.showThumbnails !== false}
        />
      )}
    </ImageSelectionContext.Provider>
  );
};
